import { GeoJSONSource } from "maplibre-gl";
import { useEffect } from "react";
import { emptyFeatureCollection } from "../../../util/geo";
import { useCaMap, useIsSection, useSelection, useSelectionFor } from "../../state";
import { Cube } from "../../state/types";
import { geometryBounds, panTo, toGeometryCollection, useSetLayoutProperty, zoomTo } from "../util";

export const BuildingsControl = ({ id }) => {
  const map = useCaMap();
  const buildings = useSelection(
    ({ state, cube }) => state === "fetched" && cube?.builtUp?.buildings
  );
  useEffect(() => {
    const data = buildings ? toGeometryCollection(buildings) : emptyFeatureCollection;
    (map?.getSource(id) as GeoJSONSource)?.setData(data);
  }, [map, buildings, id]);

  // Highlight the building selected in the report. Runs after the setData
  // effect above (which resets feature-state), so it re-applies when buildings
  // change too. Feature ids come from the source's promoteId="extId".
  const selectedId = useSelectionFor<string>("building");
  useEffect(() => {
    if (!map?.getSource(id)) return;
    map.removeFeatureState({ source: id });
    if (selectedId == null) return;
    map.setFeatureState({ source: id, id: selectedId }, { selected: true });

    // Pan to the building (keeping the current zoom so the parcel stays in
    // context) when it isn't already in view.
    const building = (buildings || []).find((b) => b.extId === selectedId);
    const bounds = geometryBounds(building?.geometry);
    if (bounds && !map.getBounds().contains(bounds.getCenter())) {
      panTo(map, bounds.getCenter());
    }
  }, [map, id, selectedId, buildings]);

  const visible = useIsSection("") ? "visible" : "none";
  useSetLayoutProperty(map, "buildings", "visibility", visible);
  return null;
};

export const SelectControl = ({ id }) => {
  // show selected field + buildings
  const map = useCaMap();
  const cube: Cube = useSelection((s) => s.state === "fetched" && s.cube);
  useEffect(() => {
    if (!map) return;
    const s = map.getSource(id) as GeoJSONSource;
    s?.setData(cube?.geometry || emptyFeatureCollection);
    if (cube?.center && !map.getBounds().contains(cube.center)) {
      // if selected feature not in view, panto feature
      zoomTo(map, cube.geometry);
    }
  }, [map, cube, id]);
  return null;
};
